import { createFileRoute, Link } from "@tanstack/react-router";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms of Service — AdKavi" },
      {
        name: "description",
        content:
          "AdKavi Terms of Service: subscription terms, billing and cancellation, user responsibilities, and AI-generated content disclaimer.",
      },
      { property: "og:title", content: "Terms of Service — AdKavi" },
      {
        property: "og:description",
        content:
          "Read the terms governing your use of AdKavi, including subscription, billing, and AI-content rules.",
      },
      { property: "og:url", content: "https://adkavi.ai/terms" },
    ],
    links: [{ rel: "canonical", href: "https://adkavi.ai/terms" }],
  }),
  component: TermsPage,
});

const UPDATED = "April 22, 2026";

function TermsPage() {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 py-16">
        <header className="mb-10">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Legal</p>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mt-2">
            Terms of Service
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">Last updated: {UPDATED}</p>
        </header>

        <div className="prose prose-neutral dark:prose-invert max-w-none space-y-8 text-sm leading-relaxed">
          <Section title="1. Agreement">
            <p>
              These Terms of Service ("Terms") govern your access to and use of AdKavi (the
              "Service"), operated by AdKavi ("AdKavi", "we", "us"). By creating an account or
              using the Service, you agree to these Terms. If you do not agree, do not use the
              Service.
            </p>
          </Section>

          <Section title="2. The Service">
            <p>
              AdKavi provides AI-assisted advertising and content tools, including ad copy
              generation, creative templates, scheduled posting, and integrations with
              third-party advertising platforms. Features and limits may change as the Service
              evolves.
            </p>
          </Section>

          <Section title="3. Accounts and Eligibility">
            <ul className="list-disc pl-5 space-y-1.5">
              <li>You must be at least 18 years old to use the Service.</li>
              <li>You are responsible for maintaining the security of your account credentials.</li>
              <li>You are responsible for all activity that occurs under your account.</li>
              <li>One person or entity per account, unless your plan explicitly permits team seats.</li>
            </ul>
          </Section>

          <Section title="4. Subscriptions, Billing & Cancellation">
            <p>
              AdKavi is offered on a subscription basis. By subscribing you authorize us, through
              our payments processor, to charge the recurring fee for your selected plan.
            </p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>
                <strong>Trial:</strong> Eligible plans may include a free trial. Unless cancelled
                before the trial ends, your subscription will automatically convert to a paid
                plan and your payment method will be charged.
              </li>
              <li>
                <strong>Auto-renewal:</strong> Subscriptions automatically renew at the end of
                each billing cycle (monthly or annual) until cancelled.
              </li>
              <li>
                <strong>Cancellation:</strong> You may cancel anytime from your billing settings.
                Cancellation takes effect at the end of the current billing cycle, and you will
                retain access until then.
              </li>
              <li>
                <strong>No refunds on monthly plans:</strong> Monthly subscription fees are
                non-refundable. To avoid being charged for the next cycle, cancel before your
                renewal date. Annual plans, taxes, and partial periods are also non-refundable
                except where required by law.
              </li>
              <li>
                <strong>Price changes:</strong> We may change pricing with reasonable advance
                notice; changes apply to subsequent billing cycles.
              </li>
              <li>
                <strong>Failed payments:</strong> If a payment fails, we may suspend your
                account until the balance is settled.
              </li>
            </ul>
          </Section>

          <Section title="5. User Responsibilities & Acceptable Use">
            <p>You agree not to use the Service to:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Violate any law, regulation, or third-party right (including intellectual property and privacy rights).</li>
              <li>Generate or distribute deceptive, defamatory, harassing, hateful, or sexually explicit content involving minors or non-consenting individuals.</li>
              <li>Run ads for prohibited products or services on connected ad platforms (e.g. Meta), or otherwise breach those platforms' policies.</li>
              <li>Attempt to reverse engineer, scrape, or interfere with the Service or its underlying APIs.</li>
              <li>Upload malware, spam, or content you do not have the right to use.</li>
            </ul>
            <p>
              You are solely responsible for the content you input, generate, schedule, and
              publish through the Service, and for ensuring it complies with applicable laws and
              the policies of any platform on which it appears.
            </p>
          </Section>

          <Section title="6. AI-Generated Content Disclaimer">
            <p>
              The Service uses artificial intelligence to assist in generating text, images,
              video, audio, and other media ("AI Output"). AI Output:
            </p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>May be inaccurate, biased, incomplete, or offensive, and may not reflect current facts.</li>
              <li>Is not legal, medical, financial, or other professional advice.</li>
              <li>May resemble existing works; you are responsible for verifying originality and clearance for your intended use.</li>
              <li>Must be reviewed by a human before publishing or running as an ad.</li>
            </ul>
            <p>
              Subject to your compliance with these Terms, you own the AI Output you generate
              for your account, except for portions provided by underlying models or third parties.
              We make no representation that AI Output is free of third-party rights.
            </p>
          </Section>

          <Section title="7. Third-Party Services">
            <p>
              AdKavi is powered by third-party APIs and platforms, including but not limited to{" "}
              <strong>OpenAI</strong>, <strong>HeyGen</strong>, <strong>ElevenLabs</strong>, and{" "}
              <strong>Meta</strong> (Facebook, Instagram). When you use features that rely on
              these providers, your inputs and outputs may be processed by them subject to their
              own terms and privacy policies. We are not responsible for the availability,
              accuracy, or behavior of third-party services.
            </p>
          </Section>

          <Section title="8. Intellectual Property">
            <p>
              The Service, including its software, design, and trademarks, is owned by AdKavi
              and its licensors and is protected by intellectual property laws. We grant you a
              limited, non-exclusive, non-transferable, revocable license to use the Service in
              accordance with these Terms. You retain ownership of content you upload, and grant
              us a worldwide, non-exclusive license to host, process, and display it solely to
              operate and improve the Service.
            </p>
          </Section>

          <Section title="9. Suspension & Termination">
            <p>
              AdKavi reserves the right to suspend, restrict, or permanently terminate your
              account and access to the Service at any time, with or without notice, for any
              reason including but not limited to:
            </p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Breach of these Terms or our Acceptable Use policy</li>
              <li>Fraudulent, abusive, or illegal activity</li>
              <li>Chargebacks or payment disputes</li>
              <li>Content that creates legal, reputational, or operational risk for AdKavi</li>
              <li>Accounts that are inactive for more than 12 months</li>
              <li>Any reason we deem necessary to protect AdKavi, our users, or third parties</li>
            </ul>
            <p>
              We are not obligated to provide a reason for termination. Fees paid prior to
              termination are non-refundable except where required by law. Upon termination,
              your right to use the Service immediately ceases.
            </p>
            <p>
              You may terminate your account at any time by cancelling your subscription and
              requesting account deletion. Sections 6, 8, 10, 11, and 12 survive termination.
            </p>
          </Section>

          <Section title="10. Disclaimers">
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY
              KIND. ADKAVI DOES NOT GUARANTEE:
            </p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>That the Service will be uninterrupted, error-free, or secure</li>
              <li>That AI-generated content will be accurate, effective, or suitable for your purpose</li>
              <li>Any specific results from running ads, including leads, sales, or revenue</li>
              <li>That connected third-party platforms (Meta, TikTok, Google) will approve, run, or maintain your ads</li>
              <li>That ad performance will meet any benchmark or expectation</li>
            </ul>
            <p>
              YOU ASSUME FULL RESPONSIBILITY FOR HOW YOU USE AI-GENERATED CONTENT AND ANY ADS
              YOU PUBLISH. ADKAVI IS A TOOL — NOT AN ADVERTISING AGENCY AND NOT RESPONSIBLE FOR
              YOUR BUSINESS RESULTS.
            </p>
          </Section>

          <Section title="11. Limitation of Liability">
            <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>ADKAVI IS NOT RESPONSIBLE FOR ANY LOST PROFITS, LOST REVENUE, LOST LEADS, LOST CLIENTS, OR LOST BUSINESS OPPORTUNITIES</li>
              <li>ADKAVI IS NOT RESPONSIBLE FOR AD ACCOUNT SUSPENSIONS, BANS, OR RESTRICTIONS BY META, TIKTOK, GOOGLE, OR ANY OTHER PLATFORM</li>
              <li>ADKAVI IS NOT RESPONSIBLE FOR ANY DECISIONS MADE BASED ON AI-GENERATED CONTENT, RECOMMENDATIONS, OR CAMPAIGN ANALYSIS</li>
              <li>ADKAVI IS NOT RESPONSIBLE FOR THIRD-PARTY SERVICE OUTAGES INCLUDING OPENAI, HEYGEN, ELEVENLABS, META, OR ANY OTHER PROVIDER</li>
              <li>ADKAVI IS NOT RESPONSIBLE FOR ANY FINANCIAL LOSSES RESULTING FROM AD CAMPAIGNS CREATED, MANAGED, OR SUGGESTED BY THE PLATFORM</li>
            </ul>
            <p>
              OUR TOTAL LIABILITY TO YOU FOR ANY CLAIM ARISING FROM USE OF THE SERVICE WILL NOT
              EXCEED THE AMOUNT YOU PAID ADKAVI IN THE 30 DAYS PRIOR TO THE EVENT GIVING RISE
              TO THE CLAIM.
            </p>
            <p>
              IF YOU ARE DISSATISFIED WITH THE SERVICE, YOUR SOLE REMEDY IS TO CANCEL YOUR
              SUBSCRIPTION.
            </p>
          </Section>

          <Section title="12. Indemnity">
            <p>
              You will indemnify and hold harmless AdKavi and its affiliates from any claim or
              demand, including reasonable attorneys' fees, arising out of your content, your
              use of the Service, or your violation of these Terms or any third-party rights.
            </p>
          </Section>

          <Section title="13. Changes to These Terms">
            <p>
              We may update these Terms from time to time. If changes are material, we will
              provide reasonable notice (e.g. via email or in-app). Your continued use of the
              Service after the effective date constitutes acceptance of the updated Terms.
            </p>
          </Section>

          <Section title="14. Governing Law">
            <p>
              These Terms are governed by the laws of the jurisdiction in which AdKavi is
              established, without regard to its conflict-of-law principles. Disputes will be
              resolved in the competent courts of that jurisdiction, unless mandatory consumer
              protection laws in your country of residence provide otherwise.
            </p>
          </Section>

          <Section title="15. Contact">
            <p>
              Questions about these Terms? Email us at{" "}
              <a href="mailto:support@adkavi.com" className="text-primary underline">
                support@adkavi.com
              </a>
              .
            </p>
            <p className="text-muted-foreground">
              See also our{" "}
              <Link to="/privacy" className="text-primary underline">
                Privacy Policy
              </Link>
              .
            </p>
          </Section>

          <Section title="16. Agency Accounts">
            <p>
              If you use AdKavi under an Agency plan, you are responsible for ensuring your
              client accounts comply with these Terms. You represent that you have obtained all
              necessary authorizations from your clients to connect their ad accounts, publish
              content on their behalf, and process their data through AdKavi. AdKavi is not
              responsible for disputes between you and your clients.
            </p>
          </Section>

          <Section title="17. No Guarantee of Results">
            <p>
              AdKavi provides AI-powered tools to assist with advertising and content creation.
              We make no guarantee, express or implied, regarding:
            </p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>The performance of any ad campaign created using AdKavi</li>
              <li>The number of leads, sales, calls, or messages you will receive</li>
              <li>The return on investment of any ad spend</li>
              <li>The approval of ads by Meta, TikTok, Google, or any platform</li>
              <li>The accuracy of AI-generated copy, creative, or recommendations</li>
            </ul>
            <p>
              Advertising results depend on many factors outside AdKavi's control including
              your offer, your market, your ad spend, platform algorithms, and economic
              conditions. You acknowledge that past results shown in marketing materials are
              examples only and not a guarantee of future performance.
            </p>
            <p>
              By using AdKavi, you accept full responsibility for your advertising decisions
              and any associated costs or outcomes.
            </p>
          </Section>

          <Section title="18. AI Video Credits Policy">
            <p>
              AI video credits are <strong>non-refundable</strong> once used. A credit is consumed
              the moment a video begins generating, regardless of whether the output is kept,
              deleted, or regenerated.
            </p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>
                <strong>Free regeneration:</strong> Each video generation includes <strong>one
                free regeneration</strong> if you are unsatisfied with the result.
              </li>
              <li>
                <strong>Additional regenerations:</strong> Any regeneration beyond the first
                consumes one credit each.
              </li>
              <li>
                Credits are not refunded for failed renders caused by content policy violations,
                invalid inputs, or third-party provider outages outside our control.
              </li>
            </ul>
          </Section>

          <Section title="19. Dispute Resolution">
            <p>
              Before filing any legal claim against AdKavi, you agree to contact us at{" "}
              <a href="mailto:support@adkavi.com" className="text-primary underline">
                support@adkavi.com
              </a>{" "}
              and give us 30 days to resolve the issue informally.
            </p>
            <p>
              If unresolved, disputes will be settled through binding arbitration in the State
              of Florida under the rules of the American Arbitration Association. You waive
              your right to a jury trial and to participate in any class action lawsuit against
              AdKavi.
            </p>
            <p>
              BY ACCEPTING THESE TERMS, YOU AGREE THAT YOUR SOLE REMEDY FOR ANY DISSATISFACTION
              WITH THE SERVICE IS TO CANCEL YOUR SUBSCRIPTION. YOU WAIVE ANY RIGHT TO BRING A
              CLAIM AGAINST ADKAVI FOR ANY AMOUNT EXCEEDING WHAT YOU PAID IN THE LAST 30 DAYS.
              ALL CLAIMS MUST BE BROUGHT WITHIN 90 DAYS OR ARE PERMANENTLY WAIVED.
            </p>
          </Section>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-xl font-semibold tracking-tight mb-3">{title}</h2>
      <div className="space-y-3 text-muted-foreground">{children}</div>
    </section>
  );
}
